import { useCallback, useEffect, useState } from "react";
import { api } from "../api.ts";
import type { Ticket } from "../types.ts";
import type { HasThreadMessage } from "../utils/threadMessage.ts";

export type AssetExchange = {
  id: number;
  ticket_id: number | null;
  kind: "replacement" | "loaner" | string;
  old_serial: string | null;
  new_serial: string | null;
  status: string;
  shipped_at: string | null;
  expected_return_at: string | null;
  returned_at: string | null;
  tracking_number: string | null;
  order_id: number | null;
  order_number: string | null;
  ship_to_address: string | null;
  warning?: string | null;
  thread_message?: import("../types.ts").Message | null;
};

type StockCandidate = {
  unit_serial: string;
  condition: string | null;
  model_code: string | null;
  device_mac: string | null;
};

const KIND_LABEL: Record<string, string> = {
  replacement: "代替え（恒久）",
  loaner: "貸し出し（一時）",
};

const STATUS_LABEL: Record<string, string> = {
  open: "手配中",
  shipped: "発送済",
  received: "回収済",
  analyzed: "分析中",
  closed: "完了",
};

export function AssetDispatchPanel({
  ticket,
  canWrite,
  onChanged,
}: {
  ticket: Ticket;
  canWrite: boolean;
  onChanged?: (result?: HasThreadMessage) => void;
}) {
  const [rows, setRows] = useState<AssetExchange[]>([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState<"replacement" | "loaner">("replacement");
  const [oldSerial, setOldSerial] = useState(ticket.unit_serial || "");
  const [newSerial, setNewSerial] = useState("");
  const [expectedReturn, setExpectedReturn] = useState("");
  const [shipTo, setShipTo] = useState("");
  const [candidates, setCandidates] = useState<StockCandidate[]>([]);

  const load = useCallback(async () => {
    try {
      const data = await api<AssetExchange[]>(`/support/exchanges?ticket_id=${ticket.id}&limit=50`);
      setRows(data);
      setErr("");
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [ticket.id]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setOldSerial(ticket.unit_serial || ""); }, [ticket.unit_serial]);

  useEffect(() => {
    const q = newSerial.trim();
    if (q.length < 2) {
      setCandidates([]);
      return;
    }
    const t = window.setTimeout(() => {
      api<{ items: StockCandidate[] }>(`/support/stock-candidates?q=${encodeURIComponent(q)}&limit=8`)
        .then((d) => setCandidates(d.items || []))
        .catch(() => setCandidates([]));
    }, 280);
    return () => window.clearTimeout(t);
  }, [newSerial]);

  const run = async (fn: () => Promise<HasThreadMessage | void>) => {
    if (busy) return;
    setBusy(true);
    setErr("");
    try {
      const result = await fn();
      await load();
      if (result) onChanged?.(result);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const create = () => run(async () => {
    const created = await api<AssetExchange>(`/support/exchanges`, {
      method: "POST",
      body: JSON.stringify({
        ticket_id: ticket.id,
        kind,
        old_serial: oldSerial.trim() || null,
        new_serial: newSerial.trim() || null,
        company: ticket.company || null,
        partner_name: ticket.purchase_route || ticket.partner_code || null,
        expected_return_at: kind === "loaner" && expectedReturn ? expectedReturn : null,
        ship_to_address: shipTo.trim() || null,
      }),
    });
    setNewSerial("");
    setExpectedReturn("");
    return created;
  });

  const openCount = rows.filter((r) => r.status !== "closed").length;

  return (
    <div className="support-asset-panel">
      {err && <div className="support-asset-panel__err">{err}</div>}
      {rows.length === 0 && (
        <div className="support-asset-panel__empty">このチケットの機体手配はまだありません。</div>
      )}
      {rows.map((r) => (
        <div key={r.id} className="support-asset-panel__row">
          <div className="support-asset-panel__row-head">
            <span className={"support-asset-kind " + (r.kind === "loaner" ? "support-asset-kind--loaner" : "")}>
              {KIND_LABEL[r.kind] || r.kind}
            </span>
            <span className="support-asset-status">{STATUS_LABEL[r.status] || r.status}</span>
            <span className="mono">{r.old_serial || "（本機未定）"}</span>
            <span aria-hidden>→</span>
            <span className="mono">{r.new_serial || "（手配先未定）"}</span>
          </div>
          <div className="support-asset-panel__meta">
            {r.expected_return_at && <span>返却予定 {r.expected_return_at}</span>}
            {r.returned_at && <span>返却 {r.returned_at}</span>}
            {r.order_number && <span>受注 {r.order_number}</span>}
            {r.tracking_number && <span>伝票 {r.tracking_number}</span>}
            {r.warning && <span className="support-asset-panel__warn">{r.warning}</span>}
          </div>
          {canWrite && r.status !== "closed" && (
            <div className="support-asset-panel__actions">
              <button type="button" disabled={busy || !r.new_serial} onClick={() => run(() => api<AssetExchange>(`/support/exchanges/${r.id}/reserve`, { method: "POST" }))}>在庫確保</button>
              <button type="button" disabled={busy || !r.new_serial} onClick={() => run(() => api<AssetExchange>(`/support/exchanges/${r.id}/create-shipment`, { method: "POST" }))}>出荷ドラフト</button>
              <button type="button" disabled={busy || !r.order_id} onClick={() => run(() => api<AssetExchange>(`/support/exchanges/${r.id}/tracking`))}>伝票取込</button>
              {r.kind === "loaner" ? (
                <button type="button" disabled={busy || !r.new_serial} onClick={() => run(() => api<AssetExchange>(`/support/exchanges/${r.id}/return`, { method: "POST", body: JSON.stringify({ outcome: "stock" }) }))}>返却→在庫</button>
              ) : (
                <button type="button" disabled={busy || !r.new_serial} onClick={() => run(() => api<AssetExchange>(`/support/exchanges/${r.id}/close`, { method: "POST" }))}>交換完了（顧客付け替え）</button>
              )}
            </div>
          )}
        </div>
      ))}

      {canWrite && (
        <div className="support-asset-panel__form">
          <div className="support-asset-panel__form-title">新規手配 {openCount > 0 ? `（進行中 ${openCount}）` : ""}</div>
          <div className="support-asset-panel__kind">
            <label>
              <input type="radio" name={`asset-kind-${ticket.id}`} checked={kind === "replacement"} onChange={() => setKind("replacement")} />
              代替え（恒久交換）
            </label>
            <label>
              <input type="radio" name={`asset-kind-${ticket.id}`} checked={kind === "loaner"} onChange={() => setKind("loaner")} />
              貸し出し（一時）
            </label>
          </div>
          <label className="support-asset-panel__field">
            不具合機シリアル
            <input value={oldSerial} onChange={(e) => setOldSerial(e.target.value)} placeholder="NX..." />
          </label>
          <label className="support-asset-panel__field">
            {kind === "loaner" ? "貸出機シリアル" : "代替機シリアル"}
            <input value={newSerial} onChange={(e) => setNewSerial(e.target.value)} placeholder="在庫から検索…" />
          </label>
          {candidates.length > 0 && (
            <div className="support-asset-panel__cands">
              {candidates.map((c) => (
                <button key={c.unit_serial} type="button" onClick={() => { setNewSerial(c.unit_serial); setCandidates([]); }}>
                  <span className="mono">{c.unit_serial}</span>
                  <span>{c.model_code} {c.condition === "REFURBISHED" ? "再生" : ""}</span>
                </button>
              ))}
            </div>
          )}
          {kind === "loaner" && (
            <label className="support-asset-panel__field">
              返却予定日
              <input type="date" value={expectedReturn} onChange={(e) => setExpectedReturn(e.target.value)} />
            </label>
          )}
          <label className="support-asset-panel__field">
            送付先
            <input value={shipTo} onChange={(e) => setShipTo(e.target.value)} placeholder="住所（任意）" />
          </label>
          <button type="button" className="support-asset-panel__submit" disabled={busy} onClick={() => void create()}>
            {busy ? "処理中…" : "手配を起票"}
          </button>
        </div>
      )}
    </div>
  );
}
